import React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const ServerError = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(ellipse at 20% 30%, ${theme.palette.error.dark}20, transparent),
          radial-gradient(ellipse at 80% 70%, ${theme.palette.error.dark}20, transparent),
          linear-gradient(135deg, ${theme.palette.background.default} 0%, #0A0A0F 100%)
        `,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* ذرات شناور */}
      {[...Array(25)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 0 }}
          animate={{
            opacity: [0, 0.4, 0],
            y: [0, -Math.random() * 180 - 40],
            x: [0, (Math.random() - 0.5) * 80],
          }}
          transition={{
            duration: Math.random() * 4 + 2,
            repeat: Infinity,
            delay: Math.random() * 4,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            bottom: `${Math.random() * 100}%`,
            width: `${Math.random() * 4 + 2}px`,
            height: `${Math.random() * 4 + 2}px`,
            background: theme.palette.error.main,
            borderRadius: "50%",
            filter: "blur(2px)",
            pointerEvents: "none",
          }}
        />
      ))}

      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, type: "spring" }}
        sx={{
          textAlign: "center",
          zIndex: 2,
          maxWidth: "700px",
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 4, md: 6 },
        }}
      >
        {/* عدد 500 */}
        <Box sx={{ position: "relative", mb: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: "100px", md: "160px" },
              fontWeight: 900,
              background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.light})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              letterSpacing: "15px",
              textShadow: `0 0 50px ${theme.palette.error.main}40`,
              position: "relative",
              "&::before, &::after": {
                content: '"500"',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "inherit",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              },
              "&::before": {
                left: "2px",
                textShadow: "none",
                transform: "skew(15deg)",
                opacity: 0.4,
                zIndex: -1,
              },
              "&::after": {
                left: "-2px",
                textShadow: "none",
                transform: "skew(-15deg)",
                opacity: 0.4,
                zIndex: -1,
              },
            }}
          >
            500
          </Typography>
        </Box>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: "1.5rem", md: "2rem" },
              color: theme.palette.text.primary,
            }}
          >
            خطای سرور!
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              mb: 4,
              fontSize: "1.1rem",
              maxWidth: "500px",
              mx: "auto",
            }}
          >
            مشکلی در سرور پیش آمده. لطفاً چند دقیقه دیگر دوباره تلاش کنید.
          </Typography>

          {/* دکمه‌ها */}
          <Box
            sx={{
              display: "flex",
              gap: 2.5,
              justifyContent: "center",
              flexWrap: "wrap",
              mb: 5,
            }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => window.location.reload()}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.error.main}, ${theme.palette.error.dark})`,
                  px: 5,
                  py: 1.5,
                  borderRadius: "50px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: `0 4px 20px ${theme.palette.error.main}60`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 30px ${theme.palette.error.main}80`,
                  },
                }}
              >
                تلاش مجدد
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate("/")}
                sx={{
                  borderColor: theme.palette.error.main,
                  color: theme.palette.error.main,
                  px: 5,
                  py: 1.5,
                  borderRadius: "50px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: theme.palette.error.light,
                    background: `${theme.palette.error.main}15`,
                  },
                }}
              >
                صفحه اصلی
              </Button>
            </motion.div>
          </Box>

          {/* لینک‌های سریع */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              justifyContent: "center",
              flexWrap: "wrap",
              pt: 3,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {[
              { label: "درباره ما", path: "/about-us" },
              { label: "تماس با ما", path: "/contact" },
              { label: "راهنما", path: "/help" },
            ].map((link, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Button
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    "&:hover": {
                      color: theme.palette.error.main,
                      background: "transparent",
                    },
                  }}
                >
                  {link.label}
                </Button>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default ServerError;
